class PreviewSelector extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this._name = "";
    this._number = "";
    this._textColor = "";
    this._borderColor = "";
    this._canvas = null;
    this._ctx = null;
  }

  static get observedAttributes() {
    return ["name", "number", "text-color", "border-color"];
  }

  get name() {
    return this._name;
  }

  set name(val) {
    this._name = val == null ? "" : String(val);
    if (this.isConnected) this._renderText();
    if (this.getAttribute("name") !== this._name)
      this.setAttribute("name", this._name);
  }

  get number() {
    return this._number;
  }

  set number(val) {
    this._number = val == null ? "" : String(val);
    if (this.isConnected) this._renderText();
    if (this.getAttribute("number") !== this._number)
      this.setAttribute("number", this._number);
  }

  attributeChangedCallback(name, oldVal, newVal) {
    if (name === "name") {
      this._name = newVal == null ? "" : String(newVal);
      this._renderText();
    } else if (name === "number") {
      this._number = newVal == null ? "" : String(newVal);
      this._renderText();
    } else if (name === "text-color") {
      this._textColor = newVal == null ? "" : String(newVal);
      this._renderText();
    } else if (name === "border-color") {
      this._borderColor = newVal == null ? "" : String(newVal);
      this._renderText();
    }
  }

  connectedCallback() {
    this._render();
    this._initCanvas();

    // Redibujar automáticamente cuando la fuente se cargue
    document.fonts.load('12px "KingsDarkColors Regular"').then(() => {
      this._renderText();
    });
  }

  disconnectedCallback() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
  }

  _render() {
    this.shadowRoot.innerHTML = `
        <style>${this._getStyles()}</style>
        <canvas id="canvas-preview-selector"></canvas>
    `;
    this.style.display = "block";
    if (this.hasAttribute("name")) this._name = this.getAttribute("name") || "";
    if (this.hasAttribute("number"))
      this._number = this.getAttribute("number") || "";
  }

  _getStyles() {
    return `
        :host {
            display: block;
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: 1000;
            pointer-events: none;
        }
        :host([inline]) {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: 10;
            pointer-events: none;
        }
        #canvas-preview-selector {
            display: block;
            width: 100%;
            height: 100%;
        }
    `;
  }

  _initCanvas() {
    this._canvas = this.shadowRoot.getElementById("canvas-preview-selector");
    if (!this._canvas) return;
    this._ctx = this._canvas.getContext("2d");

    this._updateSize();

    this._resizeObserver = new ResizeObserver(() => this._updateSize());
    this._resizeObserver.observe(this);
  }

  _updateSize() {
    if (!this._canvas) return;
    const rect = this.getBoundingClientRect();
    const w = Math.max(1, Math.floor(rect.width));
    const h = Math.max(1, Math.floor(rect.height));
    const ratio = window.devicePixelRatio || 1;

    this._canvas.width = Math.floor(w * ratio);
    this._canvas.height = Math.floor(h * ratio);
    this._canvas.style.width = `${w}px`;
    this._canvas.style.height = `${h}px`;

    if (this._ctx && this._ctx.setTransform) {
      this._ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    }

    this._renderText();
  }

  _renderText() {
    if (!this._ctx || !this._canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const cw = this._canvas.width;
    const ch = this._canvas.height;

    this._ctx.clearRect(0, 0, cw, ch);
    const logicalWidth = cw / ratio;
    const logicalHeight = ch / ratio;

    this._ctx.save();

    // Nombre
    if (this._name) {
      const nameFontSize = Math.max(
        12,
        Math.round(Math.min(logicalWidth, logicalHeight) * 0.12),
      );

      this._ctx.font = `${nameFontSize}px 'KingsDarkColors Regular', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial`;
      this._ctx.textAlign = "center";
      this._ctx.textBaseline = "middle";

      const text = this._name.toUpperCase();

      const centerX = logicalWidth * 0.5;
      const centerY = logicalHeight * 0.28;

      this._ctx.save();
      this._ctx.lineWidth = 2;
      this._ctx.fillStyle = this._textColor ?? "rgba(0,0,0,0.7)";

      // Texto recto centrado
      //this._ctx.strokeText(text, centerX, centerY);
      this._ctx.fillText(text, centerX, centerY);

      this._ctx.restore();
    }

    // Número
    if (this._number) {
      const numberFontSize = Math.max(
        12,
        Math.round(Math.min(logicalWidth, logicalHeight) * 0.5),
      );
      this._ctx.fillStyle = this._textColor ?? "rgba(0,0,0,0.9)";
      this._ctx.font = `${numberFontSize}px 'KingsDarkColors Regular', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial`;
      this._ctx.textAlign = "center";
      this._ctx.textBaseline = "middle";
      this._ctx.lineWidth = 2;
      this._ctx.strokeStyle = "black";

      this._ctx.fillText(
        this._number,
        logicalWidth * 0.5,
        logicalHeight * 0.49,
      );
    }

    this._ctx.restore();
  }
}

customElements.define("preview-selector", PreviewSelector);
